import React, { useMemo, useState } from "react";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { formatTT } from "../lib/api";
import { Home as HomeIcon, Info, ArrowRight } from "lucide-react";
import MortgagePreQualDialog from "../components/MortgagePreQualDialog";

// --- Stamp duty calculation (Trinidad & Tobago, standard buyer) ---
// Marginal tiered bands. Source: BIR / Stamp Duty Act schedules.
const STAMP_DUTY_BANDS = [
    { upTo: 850000, rate: 0 },
    { upTo: 1250000, rate: 0.03 },
    { upTo: 1750000, rate: 0.05 },
    { upTo: Infinity, rate: 0.075 },
];

const calcPropertyStampDuty = (price) => {
    let remaining = price;
    let lower = 0;
    let total = 0;
    const breakdown = [];
    for (const band of STAMP_DUTY_BANDS) {
        const bandSize = band.upTo - lower;
        const portion = Math.max(0, Math.min(remaining, bandSize));
        const duty = portion * band.rate;
        breakdown.push({
            from: lower,
            to: band.upTo,
            rate: band.rate,
            amountInBand: portion,
            duty,
        });
        total += duty;
        remaining -= portion;
        lower = band.upTo;
        if (remaining <= 0) break;
    }
    return { total, breakdown };
};

// Mortgage stamp duty: exempt up to $450,000 mortgage. ~0.2% on the full amount above $450,000.
// First-time buyers exempt up to $1,500,000.
const calcMortgageStampDuty = (mortgageAmount, isFTB) => {
    const ftbThreshold = 1500000;
    const stdThreshold = 450000;
    const threshold = isFTB ? ftbThreshold : stdThreshold;
    if (mortgageAmount <= threshold) return 0;
    return (mortgageAmount - threshold) * 0.002;
};

const Row = ({ label, value, sub, testid, bold = false }) => (
    <div className={`flex items-baseline justify-between py-2.5 border-b border-[#0B192C]/10 last:border-0 ${bold ? "font-mono-body" : ""}`}>
        <div className="text-sm text-[#4A5568]">
            {label}
            {sub && <div className="text-xs text-[#718096] mt-0.5">{sub}</div>}
        </div>
        <div className={`font-mono-body ${bold ? "text-lg text-[#0B192C]" : "text-sm text-[#0B192C]"}`} data-testid={testid}>
            {value}
        </div>
    </div>
);

const MortgageCalculator = () => {
    const [price, setPrice] = useState(1500000);
    const [downPaymentPct, setDownPaymentPct] = useState(10);
    const [rate, setRate] = useState(6.5);
    const [years, setYears] = useState(25);
    const [isFTB, setIsFTB] = useState(false);
    const [includeTitleInsurance, setIncludeTitleInsurance] = useState(false);

    const calc = useMemo(() => {
        const downPayment = price * (downPaymentPct / 100);
        const mortgageAmount = Math.max(0, price - downPayment);

        // Monthly payment (amortised)
        const months = years * 12;
        const r = rate / 100 / 12;
        const monthlyPayment = r === 0
            ? mortgageAmount / months
            : (mortgageAmount * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
        const totalPaid = monthlyPayment * months;
        const totalInterest = totalPaid - mortgageAmount;

        // Property stamp duty
        const { total: stampDuty, breakdown: stampDutyBreakdown } = calcPropertyStampDuty(price);

        // Other closing costs (mid-band estimates per user spec)
        const legalFees = price * 0.02;            // mid of 1.5%–2.5%
        const valuation = 2500;                    // mid of $1,500–$4,000
        const landRegistry = 350;                  // mid of $200–$500
        const mortgageArrangement = mortgageAmount * 0.01; // typical ~1% (lender varies)
        const mortgageStampDuty = calcMortgageStampDuty(mortgageAmount, isFTB);
        const titleInsurance = includeTitleInsurance ? 2250 : 0; // mid of $1,500–$3,000

        const closingCosts =
            stampDuty +
            legalFees +
            valuation +
            landRegistry +
            mortgageArrangement +
            mortgageStampDuty +
            titleInsurance;

        const cashAtClosing = downPayment + closingCosts;

        return {
            downPayment,
            mortgageAmount,
            monthlyPayment,
            totalPaid,
            totalInterest,
            stampDuty,
            stampDutyBreakdown,
            legalFees,
            valuation,
            landRegistry,
            mortgageArrangement,
            mortgageStampDuty,
            titleInsurance,
            closingCosts,
            cashAtClosing,
        };
    }, [price, downPaymentPct, rate, years, isFTB, includeTitleInsurance]);

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12" data-testid="mortgage-calculator-page">
            {/* Header */}
            <div className="mb-10">
                <div className="label-overline text-[#1E3A2B] mb-2 flex items-center gap-2">
                    <HomeIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Tools · Trinidad &amp; Tobago
                </div>
                <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight mb-3">
                    Mortgage &amp; Stamp Duty Calculator
                </h1>
                <p className="text-sm text-[#4A5568] max-w-2xl">
                    Estimate your monthly mortgage payment, marginal stamp duty across the four T&amp;T bands,
                    and the full cash you&rsquo;ll need at closing — including legal fees, valuation, and mortgage stamp duty.
                </p>
            </div>

            <div className="grid lg:grid-cols-5 gap-8">
                {/* Controls */}
                <div className="lg:col-span-3 grid-card bg-white p-8 space-y-8">
                    {/* Property price */}
                    <div>
                        <div className="flex items-baseline justify-between mb-3">
                            <div className="label-overline">Property Price</div>
                            <div className="font-mono-body text-2xl" data-testid="mc-price-display">{formatTT(price)}</div>
                        </div>
                        <Slider
                            data-testid="mc-price-slider"
                            value={[price]}
                            onValueChange={(v) => setPrice(v[0])}
                            min={300000}
                            max={8000000}
                            step={25000}
                        />
                        <div className="flex justify-between mt-1 text-xs text-[#718096] font-mono-body">
                            <span>{formatTT(300000)}</span>
                            <span>{formatTT(8000000)}</span>
                        </div>
                    </div>

                    {/* Down payment */}
                    <div>
                        <div className="flex items-baseline justify-between mb-3">
                            <div className="label-overline">Down Payment</div>
                            <div className="font-mono-body text-2xl" data-testid="mc-downpayment-display">
                                {downPaymentPct}% · {formatTT(calc.downPayment)}
                            </div>
                        </div>
                        <Slider
                            data-testid="mc-downpayment-slider"
                            value={[downPaymentPct]}
                            onValueChange={(v) => setDownPaymentPct(v[0])}
                            min={0}
                            max={50}
                            step={1}
                        />
                        <div className="flex justify-between mt-1 text-xs text-[#718096] font-mono-body">
                            <span>0%</span>
                            <span>50%</span>
                        </div>
                    </div>

                    {/* Interest rate */}
                    <div>
                        <div className="flex items-baseline justify-between mb-3">
                            <div className="label-overline">Mortgage Rate</div>
                            <div className="font-mono-body text-2xl" data-testid="mc-rate-display">{rate.toFixed(2)}%</div>
                        </div>
                        <Slider
                            data-testid="mc-rate-slider"
                            value={[rate]}
                            onValueChange={(v) => setRate(v[0])}
                            min={3}
                            max={15}
                            step={0.25}
                        />
                        <div className="flex justify-between mt-1 text-xs text-[#718096] font-mono-body">
                            <span>3%</span>
                            <span>15%</span>
                        </div>
                    </div>

                    {/* Term */}
                    <div>
                        <div className="flex items-baseline justify-between mb-3">
                            <div className="label-overline">Term</div>
                            <div className="font-mono-body text-2xl" data-testid="mc-term-display">{years} years</div>
                        </div>
                        <Slider
                            data-testid="mc-term-slider"
                            value={[years]}
                            onValueChange={(v) => setYears(v[0])}
                            min={5}
                            max={35}
                            step={1}
                        />
                        <div className="flex justify-between mt-1 text-xs text-[#718096] font-mono-body">
                            <span>5 yr</span>
                            <span>35 yr</span>
                        </div>
                    </div>

                    {/* Toggles */}
                    <div className="grid sm:grid-cols-2 gap-4 pt-2">
                        <div className="flex items-center justify-between border border-[#0B192C]/10 px-4 py-3">
                            <div>
                                <div className="label-overline text-[#0A4F49]">First-Time Buyer</div>
                                <div className="text-xs text-[#718096] mt-0.5">Mortgage stamp duty exempt up to TT$1.5M</div>
                            </div>
                            <Switch
                                data-testid="mc-ftb-toggle"
                                checked={isFTB}
                                onCheckedChange={setIsFTB}
                            />
                        </div>
                        <div className="flex items-center justify-between border border-[#0B192C]/10 px-4 py-3">
                            <div>
                                <div className="label-overline text-[#0A4F49]">Title Insurance</div>
                                <div className="text-xs text-[#718096] mt-0.5">Optional · ~TT$2,250</div>
                            </div>
                            <Switch
                                data-testid="mc-title-toggle"
                                checked={includeTitleInsurance}
                                onCheckedChange={setIncludeTitleInsurance}
                            />
                        </div>
                    </div>
                </div>

                {/* Results */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="grid-card bg-[#0B192C] text-[#F8F7F4] p-8">
                        <div className="label-overline opacity-70 mb-2">Monthly Payment</div>
                        <div className="font-serif-display text-5xl tracking-tight" data-testid="mc-monthly-result">
                            {formatTT(calc.monthlyPayment)}
                        </div>
                        <div className="text-xs opacity-70 mt-1 font-mono-body">
                            on {formatTT(calc.mortgageAmount)} over {years} yr
                        </div>
                    </div>

                    <div className="grid-card bg-white p-6">
                        <div className="label-overline text-[#718096] mb-1">Property Stamp Duty</div>
                        <div className="font-mono-body text-2xl text-[#0A4F49]" data-testid="mc-stampduty-result">
                            {formatTT(calc.stampDuty)}
                        </div>
                        <div className="text-xs text-[#718096] mt-1">Marginal across 4 bands</div>
                    </div>

                    <div className="grid-card bg-white p-6">
                        <div className="label-overline text-[#718096] mb-1">Total Closing Costs</div>
                        <div className="font-mono-body text-2xl" data-testid="mc-closing-result">{formatTT(calc.closingCosts)}</div>
                    </div>

                    <div className="grid-card bg-[#1E3A2B] text-[#F8F7F4] p-6">
                        <div className="label-overline opacity-70 mb-1">Cash Needed at Closing</div>
                        <div className="font-mono-body text-3xl" data-testid="mc-cash-result">{formatTT(calc.cashAtClosing)}</div>
                        <div className="text-xs opacity-70 mt-1">Down payment + all closing costs</div>
                    </div>

                    {/* Pre-Qualification CTA */}
                    <MortgagePreQualDialog
                        context={{
                            price,
                            downPayment: calc.downPayment,
                            mortgageAmount: calc.mortgageAmount,
                            monthlyPayment: calc.monthlyPayment,
                            rate,
                            years,
                            stampDuty: calc.stampDuty,
                            closingCosts: calc.closingCosts,
                            cashAtClosing: calc.cashAtClosing,
                            isFTB,
                        }}
                        trigger={
                            <button
                                data-testid="mc-prequal-cta"
                                className="w-full group flex items-center justify-between px-6 py-4 bg-[#D1A77E] text-[#0B192C] hover:bg-[#c39866] transition-colors"
                            >
                                <div className="text-left">
                                    <div className="label-overline">Get Pre-Qualified</div>
                                    <div className="text-xs opacity-80 mt-0.5">Send these numbers to a TT lender</div>
                                </div>
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={1.75} />
                            </button>
                        }
                    />
                </div>
            </div>

            {/* Detailed breakdown */}
            <div className="grid lg:grid-cols-2 gap-8 mt-12">
                {/* Stamp duty breakdown */}
                <div className="grid-card bg-white p-8">
                    <div className="label-overline text-[#0A4F49] mb-1">Stamp Duty · Band Breakdown</div>
                    <h2 className="font-serif-display text-2xl tracking-tight mb-4">How yours is calculated</h2>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="mc-stampduty-table">
                            <thead>
                                <tr className="border-b border-[#0B192C]/20">
                                    <th className="text-left py-2 label-overline text-[#718096]">Band</th>
                                    <th className="text-right py-2 label-overline text-[#718096]">In Band</th>
                                    <th className="text-right py-2 label-overline text-[#718096]">Rate</th>
                                    <th className="text-right py-2 label-overline text-[#718096]">Duty</th>
                                </tr>
                            </thead>
                            <tbody>
                                {calc.stampDutyBreakdown.map((b, i) => (
                                    <tr key={i} className="border-b border-[#0B192C]/5">
                                        <td className="py-2.5 text-[#4A5568]">
                                            {b.from === 0
                                                ? `Up to ${formatTT(b.to)}`
                                                : b.to === Infinity
                                                ? `Over ${formatTT(b.from)}`
                                                : `${formatTT(b.from)} – ${formatTT(b.to)}`}
                                        </td>
                                        <td className="py-2.5 text-right font-mono-body">{formatTT(b.amountInBand)}</td>
                                        <td className="py-2.5 text-right font-mono-body">{(b.rate * 100).toFixed(1)}%</td>
                                        <td className="py-2.5 text-right font-mono-body text-[#0A4F49]">{formatTT(b.duty)}</td>
                                    </tr>
                                ))}
                                <tr className="bg-[#EAE7E1]/40">
                                    <td colSpan={3} className="py-3 label-overline text-[#0B192C]">Total</td>
                                    <td className="py-3 text-right font-mono-body text-lg text-[#0B192C]">{formatTT(calc.stampDuty)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Closing costs breakdown */}
                <div className="grid-card bg-white p-8">
                    <div className="label-overline text-[#0A4F49] mb-1">Closing Costs · Itemised</div>
                    <h2 className="font-serif-display text-2xl tracking-tight mb-4">What you&rsquo;ll pay at signing</h2>
                    <div className="divide-y divide-[#0B192C]/10">
                        <Row label="Property stamp duty" value={formatTT(calc.stampDuty)} testid="mc-line-stampduty" />
                        <Row label="Legal fees" sub="2.0% (mid of 1.5%–2.5%)" value={formatTT(calc.legalFees)} testid="mc-line-legal" />
                        <Row label="Property valuation" sub="$1,500 – $4,000 TTD" value={formatTT(calc.valuation)} testid="mc-line-valuation" />
                        <Row label="Land Registry fee" sub="$200 – $500 TTD" value={formatTT(calc.landRegistry)} testid="mc-line-registry" />
                        <Row label="Mortgage arrangement fee" sub="~1% (varies by lender)" value={formatTT(calc.mortgageArrangement)} testid="mc-line-arrangement" />
                        <Row
                            label="Mortgage stamp duty"
                            sub={isFTB ? "FTB exempt up to $1.5M · 0.2% above" : "Exempt up to $450k · 0.2% above"}
                            value={formatTT(calc.mortgageStampDuty)}
                            testid="mc-line-mortgagestamp"
                        />
                        {includeTitleInsurance && (
                            <Row label="Title insurance" sub="Optional · $1,500 – $3,000 TTD" value={formatTT(calc.titleInsurance)} testid="mc-line-title" />
                        )}
                        <Row label="Total closing costs" value={formatTT(calc.closingCosts)} testid="mc-line-total" bold />
                    </div>
                </div>
            </div>

            {/* Educational content */}
            <div className="mt-16 grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">
                    <section>
                        <div className="label-overline text-[#0A4F49] mb-2 flex items-center gap-2">
                            <Info className="w-3.5 h-3.5" strokeWidth={1.5} /> Reference
                        </div>
                        <h2 className="font-serif-display text-3xl tracking-tight mb-4">
                            How Is Stamp Duty Calculated in Trinidad?
                        </h2>
                        <p className="text-sm text-[#4A5568] leading-relaxed mb-3">
                            Stamp duty in T&amp;T is calculated on a <strong>marginal</strong> (tiered) basis — meaning
                            the rate only applies to the portion of the price within that band, not the entire price.
                        </p>
                        <ol className="list-decimal pl-5 text-sm text-[#4A5568] space-y-1.5 leading-relaxed">
                            <li>Split the purchase price across the four bands</li>
                            <li>Multiply each portion by the applicable rate</li>
                            <li>Sum the results</li>
                        </ol>
                    </section>

                    <section className="grid-card bg-[#EAE7E1]/40 p-6">
                        <div className="label-overline text-[#1E3A2B] mb-3">Worked Example · TT$2,000,000 property</div>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-[#0B192C]/20">
                                    <th className="text-left py-2 label-overline text-[#718096]">Band</th>
                                    <th className="text-right py-2 label-overline text-[#718096]">In Band</th>
                                    <th className="text-right py-2 label-overline text-[#718096]">Rate</th>
                                    <th className="text-right py-2 label-overline text-[#718096]">Duty</th>
                                </tr>
                            </thead>
                            <tbody className="font-mono-body">
                                <tr className="border-b border-[#0B192C]/10">
                                    <td className="py-2 text-[#4A5568]">Up to $850,000</td>
                                    <td className="py-2 text-right">$850,000</td>
                                    <td className="py-2 text-right">0%</td>
                                    <td className="py-2 text-right">$0</td>
                                </tr>
                                <tr className="border-b border-[#0B192C]/10">
                                    <td className="py-2 text-[#4A5568]">$850,001 – $1,250,000</td>
                                    <td className="py-2 text-right">$400,000</td>
                                    <td className="py-2 text-right">3%</td>
                                    <td className="py-2 text-right">$12,000</td>
                                </tr>
                                <tr className="border-b border-[#0B192C]/10">
                                    <td className="py-2 text-[#4A5568]">$1,250,001 – $1,750,000</td>
                                    <td className="py-2 text-right">$500,000</td>
                                    <td className="py-2 text-right">5%</td>
                                    <td className="py-2 text-right">$25,000</td>
                                </tr>
                                <tr className="border-b border-[#0B192C]/10">
                                    <td className="py-2 text-[#4A5568]">Over $1,750,000</td>
                                    <td className="py-2 text-right">$250,000</td>
                                    <td className="py-2 text-right">7.5%</td>
                                    <td className="py-2 text-right">$18,750</td>
                                </tr>
                                <tr>
                                    <td colSpan={3} className="py-3 label-overline text-[#0B192C]">Total</td>
                                    <td className="py-3 text-right text-lg text-[#0A4F49]">$55,750</td>
                                </tr>
                            </tbody>
                        </table>
                    </section>

                    <section>
                        <h2 className="font-serif-display text-3xl tracking-tight mb-4">
                            What Other Closing Costs Should I Budget For?
                        </h2>
                        <p className="text-sm text-[#4A5568] leading-relaxed mb-4">
                            Stamp duty is the biggest single line, but it isn&rsquo;t the only one. Budget for all of the following:
                        </p>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-[#0B192C]/20">
                                        <th className="text-left py-2 label-overline text-[#718096]">Cost</th>
                                        <th className="text-right py-2 label-overline text-[#718096]">Typical Range</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b border-[#0B192C]/10">
                                        <td className="py-2.5 text-[#4A5568]">Stamp duty</td>
                                        <td className="py-2.5 text-right font-mono-body">Varies by price (use calculator above)</td>
                                    </tr>
                                    <tr className="border-b border-[#0B192C]/10">
                                        <td className="py-2.5 text-[#4A5568]">Legal fees (attorney)</td>
                                        <td className="py-2.5 text-right font-mono-body">1.5% – 2.5% of purchase price</td>
                                    </tr>
                                    <tr className="border-b border-[#0B192C]/10">
                                        <td className="py-2.5 text-[#4A5568]">Property valuation</td>
                                        <td className="py-2.5 text-right font-mono-body">$1,500 – $4,000 TTD</td>
                                    </tr>
                                    <tr className="border-b border-[#0B192C]/10">
                                        <td className="py-2.5 text-[#4A5568]">Land Registry fee</td>
                                        <td className="py-2.5 text-right font-mono-body">~$200 – $500 TTD</td>
                                    </tr>
                                    <tr className="border-b border-[#0B192C]/10">
                                        <td className="py-2.5 text-[#4A5568]">Mortgage arrangement fee</td>
                                        <td className="py-2.5 text-right font-mono-body">Varies by lender (TTMB, Republic, RBC, Scotiabank)</td>
                                    </tr>
                                    <tr className="border-b border-[#0B192C]/10">
                                        <td className="py-2.5 text-[#4A5568]">Mortgage stamp duty</td>
                                        <td className="py-2.5 text-right font-mono-body">
                                            Exempt ≤ $450k; ~0.2% above. FTB exempt up to $1.5M
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="py-2.5 text-[#4A5568]">Title insurance</td>
                                        <td className="py-2.5 text-right font-mono-body">Optional · ~$1,500 – $3,000 TTD</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>

                {/* Sticky disclaimer rail */}
                <aside className="space-y-4">
                    <div className="grid-card bg-[#0B192C] text-[#F8F7F4] p-6">
                        <div className="label-overline opacity-70 mb-2">A quick note</div>
                        <p className="text-sm leading-relaxed opacity-90">
                            Estimates are based on published BIR schedules and typical T&amp;T market ranges.
                            Final figures depend on your lender, attorney, and the property&rsquo;s valuation.
                            Always confirm with a licensed conveyancer before signing.
                        </p>
                    </div>
                    <div className="grid-card bg-white p-6">
                        <div className="label-overline text-[#0A4F49] mb-2">Ready to apply?</div>
                        <p className="text-sm text-[#4A5568] mb-4 leading-relaxed">
                            Compare mortgage rates from Trinidad &amp; Tobago&rsquo;s major lenders side-by-side.
                        </p>
                        <a
                            href="/products?category=mortgage"
                            data-testid="mc-cta-products"
                            className="inline-block px-4 py-2 bg-[#0A4F49] text-[#F8F7F4] label-overline hover:bg-[#1E3A2B] transition-colors"
                        >
                            See Mortgages →
                        </a>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default MortgageCalculator;
